import { PhoneCall } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errText } from "@/components/pipeline/utils";

const Preview = ({ preview, label }) => (
  <div className="cs-preview" data-testid="start-session-preview">
    <p><b>Liste appelée :</b> {label || "liste affichée"} · <b data-testid="start-session-count">{preview ? (preview.count >= 100 ? "les 100 premières fiches avec téléphone (dans l'ordre des relances)" : `${preview.count} fiche${preview.count > 1 ? "s" : ""} avec téléphone`) : "calcul…"}</b></p>
    {preview?.items?.length > 0 && <ol>{preview.items.map((it, i) => <li key={i}><strong>{it.title}</strong>{it.subtitle && <small> · {it.subtitle}</small>}</li>)}{preview.count > preview.items.length && <li className="cs-preview__more">… et {preview.count - preview.items.length} autres</li>}</ol>}
  </div>
);

// Démarre une « Session d'appels » sur la liste filtrée (pipeline ou fichier de prospection)
export const StartSessionButton = ({ body, count, label = "", testid = "start-call-session" }) => {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [scripts, setScripts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState("");
  const [preview, setPreview] = useState(null);
  const [script, setScript] = useState(() => localStorage.getItem("cs-script") || "");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (!open) return;
    setPreview(null);
    api.get("/call-scripts").then(({ data }) => setScripts(data)).catch(() => {});
    api.post("/call-sessions/preview", body).then(({ data }) => setPreview(data)).catch(() => setPreview({ count: 0, items: [] }));
    if (admin) api.get("/users").then(({ data }) => setAgents(data.filter((u) => u.active !== false && !u.invite_pending))).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const start = async () => {
    setBusy(true);
    localStorage.setItem("cs-script", script);
    try {
      const { data } = await api.post("/call-sessions", { ...body, label, script_id: script || null, assignee_id: agent || null });
      if (agent && agent !== user.id) { toast.success("Session confiée : l'agent la retrouve dans son Cockpit."); setOpen(false); } else navigate(`/session-appels/${data.id}`);
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" className="primary-button cs-start" onClick={() => setOpen(true)} data-testid={testid}><PhoneCall size={15} /> Session d'appels</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="start-session-dialog">
          <DialogHeader>
            <DialogTitle>Lancer une session d'appels (mode rafale)</DialogTitle>
            <DialogDescription>L'agent enchaîne les fiches ci-dessous une par une{count ? ` (${count} au maximum)` : ""} : il appelle depuis le téléphone affiché à l'écran, choisit l'issue (tag), ajoute une note si besoin, puis passe à la fiche suivante. Le débrief se fait à la fin, pré-rempli par l'IA.</DialogDescription>
          </DialogHeader>
          <Preview preview={preview} label={label} />
          {admin && (
            <label className="cs-field">Qui appelle ?
              <select value={agent} onChange={(e) => setAgent(e.target.value)} data-testid="start-session-agent">
                <option value="">Moi ({user.name})</option>
                {agents.filter((a) => a.id !== user.id).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </label>
          )}
          <label className="cs-field">Script d'appel (facultatif)
            <select value={script} onChange={(e) => setScript(e.target.value)} data-testid="start-session-script">
              <option value="">Aucun script</option>
              {scripts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <DialogFooter>
            <button type="button" className="secondary-button" onClick={() => setOpen(false)}>Annuler</button>
            <button type="button" className="primary-button" disabled={busy || !preview?.count} onClick={start} data-testid="start-session-confirm">{busy ? "Préparation…" : agent && agent !== user.id ? "Confier la session" : "Démarrer"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
